import { Archive, BarChart3, BookOpen, Briefcase, BriefcaseBusiness, CalendarCheck, ClipboardCheck, ClipboardList, FileText, GitBranch, Grid3x3, HeartHandshake, InboxIcon, Kanban, LayoutDashboard, Library, Link2, ListChecks, LogOut, Menu, MessageSquare, PieChart, Plug, Quote, ScanSearch, Smile, Target, Trophy, UserCheck, Users } from 'lucide-react'
import { NavLink } from 'react-router-dom'
import { toast } from 'sonner'

import type { ReactElement } from 'react'

import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { useSignOut } from '@/features/auth/hooks/use-sign-out'
import { cn } from '@/lib/cn'
import { hasAnyPermission } from '@/lib/permissions'
import { useAuth } from '@/providers/auth-context'

interface SidebarItem {
  to: string
  label: string
  icon: ReactElement
  requiresAny?: readonly string[]
}

interface SidebarSection {
  label: string
  items: readonly SidebarItem[]
}

const SECTIONS: readonly SidebarSection[] = [
  {
    label: 'Recrutamento & Seleção',
    items: [
      { to: '/app', label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" />, requiresAny: ['view:dashboard'] },
      { to: '/app/solicitacoes', label: 'Solicitações', icon: <ClipboardList className="h-4 w-4" />, requiresAny: ['view:solicitacoes'] },
      { to: '/app/caixa-solicitacoes', label: 'Caixa de Solicitações', icon: <InboxIcon className="h-4 w-4" />, requiresAny: ['manage:solicitacoes'] },
      { to: '/app/tipos-vagas', label: 'Tipos de Vaga', icon: <ListChecks className="h-4 w-4" />, requiresAny: ['view:tipos-vagas'] },
      { to: '/app/vagas', label: 'Vagas', icon: <Briefcase className="h-4 w-4" />, requiresAny: ['view:vagas'] },
      { to: '/app/modelos-proposta', label: 'Modelos de Proposta', icon: <FileText className="h-4 w-4" />, requiresAny: ['view:propostas'] },
      { to: '/app/banco-talentos', label: 'Banco de Talentos', icon: <Archive className="h-4 w-4" />, requiresAny: ['view:banco-talentos'] },
      { to: '/app/perfis', label: 'Biblioteca de Perfis', icon: <Library className="h-4 w-4" />, requiresAny: ['view:perfis'] },
    ],
  },
  {
    label: 'Processo Seletivo',
    items: [
      { to: '/app/triagem', label: 'Triagem', icon: <ScanSearch className="h-4 w-4" />, requiresAny: ['view:triagem'] },
      { to: '/app/kanban', label: 'Kanban', icon: <Kanban className="h-4 w-4" />, requiresAny: ['view:kanban'] },
      { to: '/app/candidatos-selecionados', label: 'Selecionados', icon: <Trophy className="h-4 w-4" />, requiresAny: ['view:kanban'] },
    ],
  },
  {
    label: 'RH & Pessoas',
    items: [
      { to: '/app/colaboradores', label: 'Colaboradores', icon: <UserCheck className="h-4 w-4" />, requiresAny: ['view:funcionarios'] },
      { to: '/app/feedbacks', label: 'Feedbacks', icon: <MessageSquare className="h-4 w-4" />, requiresAny: ['view:feedbacks'] },
      { to: '/app/feedbacks/dashboard', label: 'Dashboard de Feedbacks', icon: <PieChart className="h-4 w-4" />, requiresAny: ['view:feedbacks'] },
      { to: '/app/feedbacks/todos', label: 'Todos os Feedbacks', icon: <ListChecks className="h-4 w-4" />, requiresAny: ['view:all-feedbacks'] },
      { to: '/app/pdis', label: 'PDIs', icon: <Target className="h-4 w-4" />, requiresAny: ['view:pdis'] },
      { to: '/app/avaliacao-desempenho', label: 'Avaliação de Desempenho', icon: <ClipboardCheck className="h-4 w-4" />, requiresAny: ['view:avaliacao-desempenho'] },
      { to: '/app/competencias', label: 'Glossário de Competências', icon: <BookOpen className="h-4 w-4" />, requiresAny: ['view:competencias'] },
      { to: '/app/mantras', label: 'Mantras', icon: <Quote className="h-4 w-4" />, requiresAny: ['manage:mantras'] },
      { to: '/app/ciclos-avaliacao', label: 'Ciclos de Avaliação', icon: <CalendarCheck className="h-4 w-4" />, requiresAny: ['manage:ciclos-avaliacao', 'calibrar:avaliacoes'] },
      { to: '/app/nine-box', label: 'Matriz de Talentos', icon: <Grid3x3 className="h-4 w-4" />, requiresAny: ['view:nine-box', 'manage:nine-box'] },
      { to: '/app/quadros-carreira', label: 'Quadro de Carreira', icon: <GitBranch className="h-4 w-4" />, requiresAny: ['view:quadro-carreira', 'manage:quadro-carreira'] },
      { to: '/app/relatorios-gerenciais', label: 'Relatórios Gerenciais', icon: <BarChart3 className="h-4 w-4" />, requiresAny: ['view:relatorios-gerenciais'] },
      { to: '/app/pesquisa-clima', label: 'Pesquisa de Clima (GPTW)', icon: <HeartHandshake className="h-4 w-4" />, requiresAny: ['manage:pesquisa-clima'] },
      { to: '/app/pesquisa-clima/responder', label: 'Responder Pesquisa de Clima', icon: <Smile className="h-4 w-4" />, requiresAny: ['responder:pesquisa-clima'] },
    ],
  },
  {
    label: 'Administração',
    items: [
      { to: '/app/cargos', label: 'Cargos', icon: <BriefcaseBusiness className="h-4 w-4" />, requiresAny: ['view:cargos'] },
      { to: '/app/cargos/mapeamento-dp', label: 'Mapeamento de cargos (DP)', icon: <Link2 className="h-4 w-4" />, requiresAny: ['manage:cargos'] },
      { to: '/app/usuarios', label: 'Usuários', icon: <Users className="h-4 w-4" />, requiresAny: ['view:usuarios'] },
      { to: '/app/configuracoes/integracoes', label: 'Integrações', icon: <Plug className="h-4 w-4" />, requiresAny: ['view:integracoes'] },
    ],
  },
]

function SidebarNav({ onNavigate }: { onNavigate?: () => void }): ReactElement {
  const { user } = useAuth()
  const { mutate: signOut, isPending } = useSignOut()
  const userPerms = user?.permissions ?? []

  const visibleSections = SECTIONS.map((section) => ({
    label: section.label,
    items: section.items.filter(
      (item) => !item.requiresAny || hasAnyPermission(userPerms, item.requiresAny),
    ),
  })).filter((section) => section.items.length > 0)

  function handleSignOut() {
    signOut(undefined, {
      onError: () => toast.error('Erro ao sair. Tente novamente.'),
    })
  }

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="border-b border-sidebar-border px-5 py-4">
        <p className="text-base font-bold tracking-tight text-sidebar-foreground">TGTalentOS</p>
        <p className="mt-0.5 text-xs text-sidebar-foreground/60">Grupo TG</p>
      </div>

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4" aria-label="Navegação principal">
        {visibleSections.map((section) => (
          <div key={section.label} className="space-y-0.5">
            <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/60">
              {section.label}
            </p>
            {section.items.map((item) => (
              <NavLink
                key={`${item.to}-${item.label}`}
                to={item.to}
                end={item.to === '/app'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'relative flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-sidebar-accent/60 font-medium text-sidebar-accent-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r before:bg-sidebar-primary'
                      : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
                  )
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-3 py-3">
        {user && (
          <div className="mb-2 px-3 py-1">
            <p className="truncate text-sm font-medium">{user.name}</p>
            <p className="truncate text-xs text-sidebar-foreground/60">{user.email}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="flex min-h-[44px] w-full items-center justify-start gap-2 px-3 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground"
          onClick={handleSignOut}
          disabled={isPending}
        >
          <LogOut className="h-4 w-4" />
          Sair
        </Button>
      </div>
    </div>
  )
}

export function Sidebar(): ReactElement {
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 self-start border-r border-sidebar-border md:flex md:flex-col">
      <SidebarNav />
    </aside>
  )
}

export function MobileSidebarTrigger({
  open,
  onOpen,
}: {
  open: boolean
  onOpen: (v: boolean) => void
}): ReactElement {
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="md:hidden"
        aria-label="Abrir menu"
        aria-expanded={open}
        onClick={() => onOpen(true)}
      >
        <Menu className="h-5 w-5" />
      </Button>

      <Sheet open={open} onOpenChange={onOpen}>
        <SheetContent side="left" className="w-64 p-0">
          <SheetTitle className="sr-only">Menu de navegação</SheetTitle>
          <SidebarNav onNavigate={() => onOpen(false)} />
        </SheetContent>
      </Sheet>
    </>
  )
}
